/**
 * @file: InlineMileageRow.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The estimated-mileage row in the reservation info panel. Unlike the picker
 * rows it edits in place: a digits-only field that commits on blur (only when
 * the value actually changed), with no edit icon. Read-only when not editable.
 */

import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useEffect, useState } from 'react';
import { TextInput, View, ViewStyle } from 'react-native';

export const InlineMileageRow = memo(
  ({
    mileage,
    editable,
    onSave,
  }: {
    mileage?: number | null;
    editable: boolean;
    onSave: (value: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const seeded = mileage != null ? String(mileage) : '';
    const [text, setText] = useState(seeded);

    // Re-seed when the upstream value changes (e.g. after a save round-trip).
    useEffect(() => {
      setText(seeded);
    }, [seeded]);

    const rowStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: rpx(44),
      borderTopWidth: 1,
      borderTopColor: LD.hairline,
    };

    const label = (
      <Typography.Text
        size={rpx(13)}
        color={LD.steel}
        style={{ marginRight: rpx(12) }}
      >
        预估里程
      </Typography.Text>
    );

    if (!editable) {
      return (
        <View style={rowStyle}>
          {label}
          <Typography.Text
            size={rpx(14)}
            color={mileage != null ? LD.ink : LD.mist}
            bold={mileage != null || undefined}
            style={{ flex: 1, textAlign: 'right' }}
          >
            {mileage != null ? `${mileage} 公里` : '—'}
          </Typography.Text>
        </View>
      );
    }

    return (
      <View style={rowStyle}>
        {label}
        <TextInput
          value={text}
          onChangeText={t => setText(t.replace(/[^0-9]/g, ''))}
          onBlur={() => {
            // Commit on blur (a number-pad has no return key, so the parent
            // ScrollView dismisses the keyboard to trigger it); skip a no-op.
            if (text !== seeded) onSave(text);
          }}
          keyboardType="number-pad"
          placeholder="—"
          placeholderTextColor={LD.mist}
          style={{
            flex: 1,
            textAlign: 'right',
            fontSize: rpx(14),
            fontFamily: FontFamily.NotoSans.Bold,
            color: LD.ink,
            padding: 0,
          }}
        />
        <Typography.Text
          size={rpx(13)}
          color={LD.mist}
          style={{ marginLeft: rpx(6) }}
        >
          公里
        </Typography.Text>
      </View>
    );
  },
);
